import { useFormik } from 'formik';
import { useRouter } from 'next/router';
import * as yup from 'yup';
import { Button, Input, Textarea, useToast } from '@netfront/ui-library';
import { useSendContactForm } from '@netfront/gelada-identity-library';
import { IContactFormFields } from './ContactForm.interfaces';

export const ContactForm = () => {
  const { push } = useRouter();
  const { handleToastError, handleToastSuccess } = useToast();
  const { handleSendContactForm, isLoading: isFormSubmitting } = useSendContactForm();

  const handleContactFormSendCompleted = async (message: string, email: string, firstname: string, lastname: string) => {
    handleToastSuccess({
      message: 'Thank you for your message! We will be in touch shortly.',
    });

    await handleSendContactForm({
      firstName: firstname,
      lastName: lastname,
      message: message,
      sender: email,
      confirmationEmailPlaceholders: [
        { key: '[FullName]', value: `${firstname} ${lastname}` },
        { key: '[Email]', value: email },
        { key: '[Message]', value: message },
      ],
      placeholders: [
        { key: '[FullName]', value: `${firstname} ${lastname}` },
        { key: '[Email]', value: email },
        { key: '[Message]', value: message },
      ],
    });

    push('/').catch((error) =>
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      }),
    );
  };

  const formik = useFormik<IContactFormFields>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      message: '',
    },
    validationSchema: yup.object().shape({
      firstName: yup.string().required('Please enter in a first name'),
      lastName: yup.string().required('Please enter in a last name'),
      email: yup.string().email('Please enter a valid email address').required('Please enter in a valid email address'),
      message: yup.string().required('Please enter in a message'),
    }),
    onSubmit: async ({ email, firstName, lastName, message }: IContactFormFields) => {
      await handleContactFormSendCompleted(message, email, firstName, lastName);
    },
  });

  const { errors, touched, handleBlur, handleChange, handleSubmit } = formik;

  return (
    <form onSubmit={handleSubmit}>
      <Input
        errorMessage={touched.firstName && errors.firstName ? errors.firstName : ''}
        id="firstName"
        labelText="First name"
        placeholder="Enter first name"
        name="firstName"
        onChange={handleChange}
        type="text"
        isRequired
      />

      <Input
        errorMessage={touched.lastName && errors.lastName ? errors.lastName : ''}
        id="lastName"
        labelText="Last name"
        placeholder="Enter last name"
        name="lastName"
        onChange={handleChange}
        type="text"
        isRequired
      />

      <Input
        errorMessage={touched.email && errors.email ? errors.email : ''}
        id="email"
        labelText="Email"
        placeholder="Enter email"
        name="email"
        onBlur={handleBlur}
        onChange={handleChange}
        type="email"
        isRequired
      />

      <Textarea
        errorMessage={touched.message && errors.message ? errors.message : ''}
        id="message"
        labelText="Message"
        placeholder="Enter message"
        name="message"
        value={formik.values.message}
        onChange={formik.handleChange}
        isRequired
      />

      <div className="text-right mb-8">
        <Button variant="accent" text="Send" type="submit" isLoading={isFormSubmitting} />
      </div>
    </form>
  );
};
